'use client';
import useEmblaCarousel from 'embla-carousel-react';
import LogoSlide from './_LogoSlide';
import { useIsLG, useIsMD } from '@repo/ui/src/utils/useMediaQuery';
import LeftArrow from '@repo/ui/src/components/Carousels/CarouselArrows/left-arrow';
import RightArrow from '@repo/ui/src/components/Carousels/CarouselArrows/right-arrow';
import { useEffect, useMemo } from 'react';
import { classNames } from '@repo/ui/src/utils/classNames';
import { MediaType, LinkProps } from '@repo/ui/src/types';

interface LogoCarouselProps {
  items?: Array<{
    image: MediaType;
    link: LinkProps;
  }>;
  header: string;
  title?: string;
  description?: string;
}

const LogoCarousel = ({ items = [], header, title, description }: LogoCarouselProps) => {
  const carouselTitle = header || title;
  const isLarge = useIsLG();
  const isMed = useIsMD();

  const slideCount = useMemo(() => {
    if (isLarge && items?.length >= 5) {
      return 5;
    }

    if (isLarge && items?.length < 5) {
      return 4;
    }
    if (isMed) {
      return 3;
    }
    return 1;
  }, [isMed, isLarge, items]);

  const hideArrows = slideCount === items?.length;

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });

  useEffect(() => {
    emblaApi?.reInit();
  }, [emblaApi, slideCount]);

  if (!items.length) return null;

  return (
    <section className="flex flex-col text-center my-10 mx-5 md:my-20 lg:mx-auto lg:max-w-[1200px]">
      <h4 className="tra-text-web-heading-4 pb-10">{carouselTitle}</h4>
      {description && <p className="tra-text-web-body-m pb-10">{description}</p>}
      <p className="sr-only">This is a carousel. Tab through slides to navigate.</p>
      <div className={classNames('grid', !hideArrows && 'md:grid-cols-[60px_auto_60px]')}>
        <button
          aria-label="previous"
          className={classNames(
            'hidden h-fit self-center border-0 bg-transparent md:block',
            hideArrows && 'md:hidden',
          )}
          onClick={() => emblaApi?.scrollPrev()}
        >
          <LeftArrow asSpan />
        </button>
        <div ref={emblaRef} className="overflow-hidden">
          <div className="flex">
            {items?.map((slide, i) => (
              <div key={i} style={{ flex: `0 0 calc(100% / ${slideCount})` }}>
                <LogoSlide slide={slide} index={i} />
              </div>
            ))}
          </div>
        </div>
        <button
          aria-label="next"
          className={classNames(
            'hidden h-fit self-center border-0 bg-transparent md:block',
            hideArrows && 'md:hidden',
          )}
          onClick={() => emblaApi?.scrollNext()}
        >
          <RightArrow asSpan />
        </button>
        <div
          className={classNames(
            'flex flex-row w-full justify-center gap-5',
            hideArrows && 'hidden',
          )}
        >
          <button
            aria-label="previous"
            className="block h-fit self-center border-0 bg-transparent md:hidden"
            onClick={() => emblaApi?.scrollPrev()}
          >
            <LeftArrow asSpan />
          </button>
          <button
            aria-label="next"
            className="h-fit self-center border-0 bg-transparent md:hidden"
            onClick={() => emblaApi?.scrollNext()}
          >
            <RightArrow asSpan />
          </button>
        </div>
      </div>
    </section>
  );
};

export default LogoCarousel;
